import type { HealthResponse, ReadyResponse, Entitlements } from '@snap-platform/shared';

const BASE_URL = '/api';

/**
 * Typed API error with code and statusCode for structured error handling.
 */
export class ApiError extends Error {
  code: string;
  statusCode: number;

  constructor(message: string, code: string, statusCode: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const sep = url.includes('?') ? '&' : '?';
  const cacheBustedUrl = `${url}${sep}_t=${Date.now()}`;

  const res = await fetch(cacheBustedUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store',
      ...(options?.headers as Record<string, string> | undefined),
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(
      body.error ?? `Request failed: ${res.status}`,
      body.code ?? 'UNKNOWN_ERROR',
      res.status,
    );
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

function request<T>(path: string, options?: RequestInit): Promise<T> {
  return fetchJson<T>(`${BASE_URL}${path}`, options);
}

function authFetch<T>(url: string, token: string, options?: RequestInit): Promise<T> {
  return fetchJson<T>(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });
}

function authRequest<T>(path: string, token: string, options?: RequestInit): Promise<T> {
  return authFetch<T>(`${BASE_URL}${path}`, token, options);
}

// Health (backend mounts these at /health and /ready, not under /api)
export function getHealth(): Promise<HealthResponse> {
  return fetchJson<HealthResponse>('/health');
}

export function getReady(): Promise<ReadyResponse> {
  return fetchJson<ReadyResponse>('/ready');
}

// Auth
export function getNonce(): Promise<{ nonce: string }> {
  return request<{ nonce: string }>('/auth/nonce', { method: 'POST' });
}

export function verifySignature(
  address: string,
  message: string,
  signature: string,
): Promise<{ token: string; walletAddress: string }> {
  return request<{ token: string; walletAddress: string }>('/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ address, signature, message }),
  });
}

export function revokeSession(token: string): Promise<void> {
  return authRequest<void>('/auth/session', token, { method: 'DELETE' });
}

// Subscription
export function createCheckoutSession(token: string): Promise<{ url: string }> {
  return authRequest<{ url: string }>('/subscription/checkout', token, {
    method: 'POST',
    body: JSON.stringify({ plan: 'monthly' }),
  });
}

// Subscription status
export function getSubscriptionStatus(token: string): Promise<{
  status: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  stripe_price_id?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  wallet_address?: string;
}> {
  return authRequest('/subscription/status', token);
}

// Support
export function submitSupportRequest(data: {
  email: string;
  name?: string;
  category: string;
  message: string;
}): Promise<{ success: boolean }> {
  return request<{ success: boolean }>('/support', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Usage
export function getUsageSummary(
  token: string,
): Promise<{ activeDays: number; totalAccessCount: number }> {
  return authFetch<{ activeDays: number; totalAccessCount: number }>(
    '/internal/usage/summary',
    token,
  );
}

// Entitlements
export function getEntitlements(token: string): Promise<Entitlements> {
  return authRequest<Entitlements>('/entitlements', token);
}

// Terms Acceptance
export function getTermsStatus(token: string): Promise<{ termsAccepted: boolean }> {
  return authRequest<{ termsAccepted: boolean }>('/terms/status', token);
}

export function acceptTerms(token: string): Promise<{ success: boolean }> {
  return authRequest<{ success: boolean }>('/terms/accept', token, { method: 'POST' });
}

// Subscription Restore
export function restoreSubscription(token: string): Promise<{
  restored: boolean;
  tier: string;
  stripeSubscriptionId?: string;
  reason?: string;
  message: string;
}> {
  return authRequest('/subscription/restore', token, { method: 'POST' });
}

// Email Recovery
export function requestEmailRecovery(
  token: string,
  email: string,
  action: 'restore' | 'transfer' = 'restore',
): Promise<{ success: boolean; message: string; sessionId: string }> {
  return authRequest('/recovery/request', token, {
    method: 'POST',
    body: JSON.stringify({ email, action }),
  });
}

export function verifyEmailRecovery(
  token: string,
  sessionId: string,
  code: string,
): Promise<{
  restored: boolean;
  tier: string;
  stripeSubscriptionId?: string;
  action: string;
  message: string;
}> {
  return authRequest('/recovery/verify', token, {
    method: 'POST',
    body: JSON.stringify({ sessionId, code }),
  });
}

// Stripe Customer Portal
export function createCustomerPortalSession(
  token: string,
): Promise<{ url: string }> {
  return authRequest<{ url: string }>('/subscription/customer-portal', token, {
    method: 'POST',
  });
}

// Conversion Tracking
export function trackConversionEvent(
  eventType: string,
  metadata?: Record<string, unknown>,
  token?: string | null,
): Promise<{ success: boolean }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetchJson<{ success: boolean }>(`${BASE_URL}/events/track`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ eventType, metadata }),
  });
}

// Snap
export function notifySnapInstalled(
  token: string,
  snapId: string,
  version: string,
): Promise<{ success: boolean }> {
  return authRequest<{ success: boolean }>('/snap/installed', token, {
    method: 'POST',
    body: JSON.stringify({ snapId, version }),
  });
}

// Token Intel (public endpoint, no auth required)
export function getTokenIntel(chain: string, token: string, wallet?: string): Promise<any> {
  const params = wallet ? `?wallet=${wallet}` : "";
  return request(`/intel/token/${chain}/${token}${params}`);
}



/**
 * Request a short-lived access token for deep intel.
 * Requires: authenticated user with session JWT.
 * Returns: { token, expiresAt, tier } or throws.
 */
export async function getIntelAccessToken(sessionToken: string): Promise<{
  token: string;
  expiresAt: number;
  tier: string;
}> {
  const res = await fetch('/api/auth/access-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionToken}`,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || 'Failed to get access token');
    (err as any).code = body.code || 'ACCESS_TOKEN_ERROR';
    (err as any).statusCode = res.status;
    throw err;
  }

  return res.json();
}

/**
 * Fetch deep intel using an access token.
 */
export async function getDeepTokenIntel(
  chain: string,
  token: string,
  accessToken: string,
  wallet?: string,
): Promise<any> {
  const params = new URLSearchParams();
  if (wallet) params.set('wallet', wallet);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const res = await fetch(`/api/intel/token/${chain}/${token}${qs}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || 'Failed to fetch deep intel');
    (err as any).code = body.code || 'DEEP_INTEL_ERROR';
    (err as any).statusCode = res.status;
    throw err;
  }

  return res.json();
}

// Web Users (public — website conversion funnel)
export function connectWebWallet(
  walletAddress: string,
): Promise<{ success: boolean; wallet_address: string; created: boolean }> {
  return request('/web/connect-wallet', {
    method: 'POST',
    body: JSON.stringify({ wallet_address: walletAddress }),
  });
}

// Web Auth (wallet signature authentication for website)
export function getWebNonce(walletAddress: string): Promise<{ nonce: string }> {
  return request<{ nonce: string }>('/web/auth/nonce', {
    method: 'POST',
    body: JSON.stringify({ wallet_address: walletAddress }),
  });
}

export function verifyWebSignature(
  walletAddress: string,
  signature: string,
  nonce: string,
): Promise<{ token: string; walletAddress: string }> {
  return request<{ token: string; walletAddress: string }>('/web/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ wallet_address: walletAddress, signature, nonce }),
  });
}

// Web Scan History
export function recordWebScan(
  webToken: string,
  chain: string,
  tokenAddress: string,
  result: Record<string, unknown>,
): Promise<{ success: boolean }> {
  return authRequest<{ success: boolean }>('/web/record-scan', webToken, {
    method: 'POST',
    body: JSON.stringify({ chain, token_address: tokenAddress, result }),
  });
}

export interface WebScanRecord {
  id: string;
  chain: string;
  token_address: string;
  result: Record<string, unknown>;
  created_at: string;
}

export function getWebScanHistory(
  webToken: string,
): Promise<{ scans: WebScanRecord[] }> {
  return authRequest<{ scans: WebScanRecord[] }>('/web/scan-history', webToken);
}

// Metered scan (authenticated, usage-tracked)
export interface WebScanUsage {
  scans_used: number;
  scan_limit: number;
  period_start: string;
  period_end: string;
}

export function webScan(
  webToken: string,
  chain: string,
  tokenAddress: string,
): Promise<any> {
  return authRequest('/web/scan', webToken, {
    method: 'POST',
    body: JSON.stringify({ chain, token_address: tokenAddress }),
  });
}

// Web Billing
export interface WebBillingStatus {
  plan: string;
  status: string;
  subscriptionId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
}

export function getWebBillingStatus(webToken: string): Promise<WebBillingStatus> {
  return authRequest<WebBillingStatus>('/web/billing/status', webToken);
}

export function createWebCheckoutSession(
  webToken: string,
  plan: 'pro' | 'elite' = 'pro',
): Promise<{ url: string }> {
  return authRequest<{ url: string }>('/web/billing/create-checkout-session', webToken, {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });
}

export function createWebPortalSession(
  webToken: string,
): Promise<{ url: string }> {
  return authRequest<{ url: string }>('/web/billing/create-portal-session', webToken, {
    method: 'POST',
  });
}
